import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { Moment } from 'moment';
import { colorsTheme } from 'resources/theme/styled/colors';
import { generateUniqId } from 'helpers/commonHelpers';
import { CountryFromQuery } from 'api/crm/countriesAPI';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { entityProfileStepValidationSchema } from 'validations/intake-form/intake-form-steps';
import { formatDateToString, formatDateToUTC } from 'helpers/dateHelpers';
import {
  EntityAgeOptions,
  NumberOfEmployeesOptions,
  NumberOfOfficesOptions,
  OrganizationAddressTypes,
} from 'enums/onboarding/crm';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { AutocompleteOption, Form, SectionIntro } from '@ui';

export type AddressFormItem = {
  id: string;
  type: OrganizationAddressTypes[];
  country: string | null;
  countryInitialOption?: AutocompleteOption<CountryFromQuery>;
  city: string;
  street: string;
  state: string;
  postal: string;
};

export interface FormValuesModel extends SubmitButtonsFormValues {
  countryOfIncorporation: string | null;
  countryOfIncorporationInitialOption?: AutocompleteOption<CountryFromQuery>;
  dateOfIncorporation: Moment | null;
  entityAge: EntityAgeOptions | null;
  registrationNumber: string;
  isRegulated: boolean | null;
  regulationCountry: string[];
  regulationCountryInitialOptions?: AutocompleteOption<CountryFromQuery>[];
  regulationExemption: string;
  hasBearerShares: boolean | null;
  numberOfEmployees: NumberOfEmployeesOptions | null;
  numberOfOffices: NumberOfOfficesOptions | null;
  isOperatingInSanctionedCountries: boolean | null;
  companyWebsite: string;
  addresses: AddressFormItem[];
}

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

const EntityProfile = ({ disabled, renderNavigateButtonInTitle }: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    intakeForm,
    navigateToNextStep,
    navigateToPreviousStep,
    updateIntakeFormFields,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel | null>(() => {
    if (!intakeForm) {
      return null;
    }

    const intakeFormFields = intakeForm.fields;

    let countryOfIncorporationInitialOption:
      | AutocompleteOption<CountryFromQuery>
      | undefined = undefined;
    if (
      intakeFormFields?.entityCountry &&
      intakeForm.metadata.countries[intakeFormFields?.entityCountry]
    ) {
      countryOfIncorporationInitialOption = {
        id: intakeFormFields.entityCountry,
        label: intakeForm.metadata.countries[intakeFormFields?.entityCountry],
      };
    }

    let regulationCountryInitialOptions:
      | AutocompleteOption<CountryFromQuery>[]
      | undefined = undefined;
    if (
      intakeFormFields?.regulationCountry &&
      intakeFormFields.regulationCountry.length
    ) {
      regulationCountryInitialOptions = intakeFormFields.regulationCountry.map(
        (e) => ({
          id: e,
          label: intakeForm.metadata.countries[e],
        }),
      );
    }

    return {
      submitType: null,
      countryOfIncorporation: intakeFormFields?.entityCountry || null,
      countryOfIncorporationInitialOption,
      dateOfIncorporation: intakeFormFields?.entityDateFormation
        ? formatDateToUTC(intakeFormFields.entityDateFormation)
        : null,
      entityAge: intakeFormFields?.entityOperationStart || null,
      registrationNumber: intakeFormFields?.registrationNumber || '',
      isRegulated:
        typeof intakeFormFields?.isRegulated === 'boolean'
          ? intakeFormFields.isRegulated
          : null,
      regulationCountry: intakeFormFields?.regulationCountry || [],
      regulationCountryInitialOptions,
      regulationExemption: intakeFormFields?.regulationExemption || '',
      hasBearerShares:
        typeof intakeFormFields?.hasBearerShares === 'boolean'
          ? intakeFormFields.hasBearerShares
          : null,
      numberOfEmployees: intakeFormFields?.numEmployees || null,
      numberOfOffices: intakeFormFields?.numOffices || null,
      isOperatingInSanctionedCountries:
        typeof intakeFormFields?.isOperatingInSanctionedCountries == 'boolean'
          ? intakeFormFields.isOperatingInSanctionedCountries
          : null,
      companyWebsite: intakeFormFields?.entityWebsite || '',
      addresses:
        intakeFormFields?.addresses && intakeFormFields.addresses.length
          ? intakeFormFields.addresses.map((e) => {
              return {
                id: e.id || `custom-${generateUniqId()}`,
                type: e.type || [],
                country: e.country || null,
                countryInitialOption: e.country
                  ? {
                      id: e.country,
                      label: intakeForm.metadata.countries[e.country],
                    }
                  : undefined,
                city: e.city || '',
                street: e.street || '',
                state: e.state || '',
                postal: e.postalCode || '',
              };
            })
          : [
              {
                id: `custom-${generateUniqId()}`,
                type: [],
                country: null,
                city: '',
                street: '',
                state: '',
                postal: '',
              },
            ],
    };
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }

    const filledLocations = values.addresses.filter(
      (e) =>
        e.type.length || e.country || e.city || e.street || e.state || e.postal,
    );
    await updateIntakeFormFields(
      {
        entityDateFormation: values.dateOfIncorporation
          ? formatDateToString(values.dateOfIncorporation, 'YYYY-MM-DD')
          : null,
        entityOperationStart: values.entityAge || null,
        registrationNumber: values.registrationNumber || null,
        isRegulated:
          typeof values.isRegulated === 'boolean' ? values.isRegulated : null,
        regulationCountry:
          values.isRegulated && values.regulationCountry.length
            ? values.regulationCountry
            : null,
        regulationExemption:
          values.isRegulated === false && values.regulationExemption
            ? values.regulationExemption
            : null,
        hasBearerShares:
          typeof values.hasBearerShares === 'boolean'
            ? values.hasBearerShares
            : null,
        numEmployees: values.numberOfEmployees || null,
        numOffices: values.numberOfOffices || null,
        isOperatingInSanctionedCountries:
          typeof values.isOperatingInSanctionedCountries == 'boolean'
            ? values.isOperatingInSanctionedCountries
            : null,
        entityWebsite: values.companyWebsite || null,
        addresses: filledLocations.length
          ? filledLocations.map((e) => ({
              id: e.id.includes('custom-') ? null : e.id,
              type: e.type,
              street: e.street,
              city: e.city,
              state: e.state,
              postalCode: e.postal,
              country: e.country || '',
            }))
          : null,
      },
      undefined,
    );
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant={disabled ? 'h5' : undefined}
        title={
          <DivAlignCenter>
            {t('intake_form.entity_profile.section_title', {
              entityName:
                intakeForm?.fields?.entityLegalName ||
                t('intake_form.entity_profile.entity_name_when_not_provided'),
            })}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="entity_profile" />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm />}
        initialValues={initialValues}
        validationSchema={entityProfileStepValidationSchema}
      />
    </>
  );
};

export default EntityProfile;
